import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useFormik } from 'formik';
import { FC, useEffect, useRef, useState } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../../assets/logo_completo.png';
import { menuRoutes } from "../../../router/menu";
import { TutorService } from "../../../services/tutor/tutorService";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SignAgreementMobile: FC = () => {

    // HOOKS

    const navigate = useNavigate();
    const { hash } = useParams<{ hash: string }>();

    // STATES

    const [signatureHash, setSignatureHash] = useState<string | undefined>();
    const [agreementLoaded, setAgreementLoaded] = useState(false);
    const [showSignature, setShowSignature] = useState(false);
    const signatureRef = useRef<SignatureCanvas>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfBlob, setPdfBlob] = useState<any | null>(null);
    const [agreementData, setAgreementData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [signingAgreement, setSigningAgreement] = useState(false);

    // FUNCTIONS

    const handleObtainAgreementInfo = async (signatureHash: string | undefined) => {
        try {
            setAgreementData({ hash: signatureHash });
        } catch (error) {
            console.error(error);
            navigate(menuRoutes.tutor_agreement.error);
        }
    };

    const handlePreviewAgreement = async (signatureHash: string | undefined) => {
        try {
            if (!signatureHash) {
                throw new Error('Hash no válido');
            }
            const service = await (new TutorService()).previewAgreementDocument(signatureHash);
            const response = service.response;
            if (response?.data) {
                let base64String;
                const fileData = response.data;
                if (pdfBlob && pdfBlob.size === fileData.byteLength) return;
                const blob = new Blob([fileData]);
                let reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    base64String = reader.result;
                    setPdfBlob(base64String);
                };
            }
        } catch (error) {
            console.error(error);
            toast.error('Error al cargar el convenio');
            navigate(menuRoutes.tutor_agreement.error);
        } finally {
            setAgreementLoaded(true);
        }
    };

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    };

    function onDocumentLoadError(error: any) {
        navigate(menuRoutes.tutor_agreement.error);
    };

    const handleSignAgreement = async (signatureHash: string) => {
        setIsLoading(true);
        setSigningAgreement(true);

        if (signatureRef.current?.isEmpty()) {
            toast.error('Por favor, realice su firma');
            setIsLoading(false);
            setSigningAgreement(false);
            return;
        }
        const signatureImage = signatureRef.current?.toDataURL();

        try {
            if (!signatureImage) {
                throw new Error('Error al obtener la firma');
            }
            const response = await (new TutorService()).signAgreementDocument(signatureHash, signatureImage);
            if (!response?.getResponse() || response.getResponse()?.status !== 200) {
                toast.error('Error al firmar el convenio');
                return;
            }
            // Redirigir a la página de convenio firmado
            navigate(menuRoutes.tutor_agreement.signed);
        } catch (error) {
            console.error(error);
            toast.error('Error al firmar el convenio');
        } finally {
            setIsLoading(false);
            setSigningAgreement(false);
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { signature: null, },
        validateOnChange: false,
        onSubmit: () => {
            if (signatureHash && signatureHash.length > 4) {
                handleSignAgreement(signatureHash);
            } else {
                toast.error('Hash inválido o nulo');
            }
        },
    });

    // USE EFFECTS

    useEffect(() => {
        const fetchData = async () => {
            if (hash && !agreementData) {
                setSignatureHash(hash);
                await handleObtainAgreementInfo(hash);
                await handlePreviewAgreement(hash);
            }
        };
        fetchData();
    }, [hash, agreementData]);

    // RENDER

    return (
        <div className="min-h-screen bg-white">
            <div className="px-4 py-6">
                {/* Header */}
                <div className="text-center mb-6">
                    <img src={fullLogo} className="h-8 mx-auto" alt="Logo de Attiva" />
                    <h1 className="text-xl font-bold mt-3">Firma de convenio</h1>
                    <p className="text-gray-600 text-sm mt-2">
                        Revise el convenio y proceda con la firma
                    </p>
                </div>

                {/* Vista del PDF */}
                {pdfBlob && agreementLoaded && (
                    <div className="mb-6">
                        <div className="flex justify-center">
                            <Document
                                file={pdfBlob}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                className="flex justify-center items-center"
                            >
                                <Page
                                    pageNumber={pageNumber}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    width={Math.min(window.innerWidth - 32, 400)}
                                />
                            </Document>
                        </div>
                        <div className="flex items-center justify-center mt-4 space-x-4">
                            <button
                                onClick={() => setPageNumber((prevPage) => Math.max(prevPage - 1, 1))}
                                disabled={pageNumber <= 1}
                                className="text-lg p-2 hover:bg-gray-200 rounded-full disabled:opacity-50"
                            >
                                <FaArrowLeft />
                            </button>
                            <span className="text-sm font-medium">
                                {pageNumber} / {numPages}
                            </span>
                            <button
                                onClick={() => setPageNumber((prevPage) => Math.min(prevPage + 1, numPages || 1))}
                                disabled={pageNumber >= (numPages || 1)}
                                className="text-lg p-2 hover:bg-gray-200 rounded-full disabled:opacity-50"
                            >
                                <FaArrowRight />
                            </button>
                        </div>
                    </div>
                )}

                {!pdfBlob && !agreementLoaded && (
                    <div className="text-center py-8">
                        <p className="text-gray-500">Cargando convenio...</p>
                    </div>
                )}

                {/* Botón para mostrar firma */}
                {!showSignature && !signingAgreement && (
                    <Button
                        type="button"
                        onClick={() => setShowSignature(true)}
                        className="w-full"
                        size="lg"
                    >
                        Firmar Convenio
                    </Button>
                )}

                {/* Formulario de firma */}
                {showSignature && !signingAgreement && (
                    <form onSubmit={formik.handleSubmit} className="space-y-4">
                        <div className="border border-gray-300 rounded-md p-2">
                            <SignatureCanvas
                                ref={signatureRef}
                                canvasProps={{
                                    width: window.innerWidth - 48,
                                    height: 150,
                                    className: 'signature-canvas',
                                }}
                            />
                            <div className="mt-3 flex justify-between space-x-2">
                                <Button
                                    type="button"
                                    onClick={() => signatureRef.current?.clear()}
                                    variant="outline"
                                    className="flex-1"
                                >
                                    Limpiar
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={() => {
                                        if (signatureRef.current?.isEmpty()) {
                                            toast.error('Por favor, realice su firma');
                                            return;
                                        }
                                        const signatureImage = signatureRef.current?.toDataURL();
                                        formik.setFieldValue('signature', signatureImage);
                                        handleObtainAgreementInfo(hash);
                                    }}
                                    isLoading={isLoading}
                                    disabled={isLoading}
                                    className="flex-1"
                                >
                                    Firmar
                                </Button>
                            </div>
                        </div>
                    </form>
                )}

                {/* Estado de firma en progreso */}
                {signingAgreement && (
                    <div className="flex flex-col justify-center items-center text-center py-8">
                        <DotLottieReact
                            src="https://lottie.host/4f0e8e8b-b9b1-4b1b-8b1b-8b1b8b1b8b1b/4f0e8e8b.lottie"
                            loop
                            autoplay
                            className="w-24 h-24"
                        />
                        <h2 className="text-lg font-semibold mt-4">Firmando convenio...</h2>
                        <p className="text-gray-600 text-sm mt-2">
                            Por favor, espere mientras procesamos su firma
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignAgreementMobile;